import logger from '../utils/logger';
export class OAuthPopupHandler {
  constructor() {
    this.popup = null;
    this.messageListener = null;
    this.checkInterval = null;
  }

  async openPopup(authUrl, popupId) {
    const width = 500;
    const height = 600;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const features = `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`;
logger.debug('UTILS', '🔵 OAuthPopupHandler: Opening popup', {
        authUrl,
        popupId,
        features
      });
    
    this.popup = window.open(authUrl, 'GoogleOAuth', features);
    
    if (!this.popup || this.popup.closed) {
      throw new Error('Popup blocked');
    }
    
    // Store popup ID in sessionStorage for recovery
    sessionStorage.setItem('oauth_popup_id', popupId);
    
    this.setupMessageListener(popupId);
    this.monitorPopup();
    
    // Return a promise that resolves/rejects based on OAuth result
    return new Promise((resolve, reject) => {
      this.resolveCallback = resolve;
      this.rejectCallback = reject;
      
      // Timeout after 5 minutes
      this.timeout = setTimeout(() => {
logger.error('UTILS', '⏱️ OAuth timeout - no response received');
        this.cleanup();
        reject(new Error('OAuth timeout - no response received'));
      }, 5 * 60 * 1000);
    });
  }
  
  setupMessageListener(expectedPopupId) {
logger.debug('UTILS', 'Setting up message listener for popup_id:', expectedPopupId);

    this.messageListener = (event) => {
      // Log all messages for debugging (development only - contains sensitive data)
logger.debug('UTILS', 'Received postMessage:', {
          origin: event.origin,
          data: event.data,
          expectedPopupId: expectedPopupId
        });
      
      // Explicit allowlist of trusted origins (security best practice)
      // Do NOT use window.location.origin - it's dynamic and could be manipulated
      const allowedOrigins = [
        // Backend API URLs
        'https://concierge-fast-api-development-712015631621.us-west3.run.app',
        'https://concierge-fast-api-production-712015631621.us-central1.run.app',
        // Frontend URLs (for local development and deployed versions)
        'https://concierge-frontend-development-712015631621.us-west3.run.app',
        'https://concierge-frontend-production-712015631621.us-central1.run.app',
        'https://concierge.kiln.com',
        // Local development
        'http://localhost:8000',
        'http://localhost:3000'
      ];
      
      if (!allowedOrigins.includes(event.origin)) {
logger.warn('UTILS', 'Ignoring message from unauthorized origin:', event.origin, 'Allowed:', allowedOrigins);
        return;
      }
      
      // Validate event.data structure more strictly
      if (!event.data || typeof event.data !== 'object') {
        return; // Ignore non-object messages
      }

      // Check if this is an OAuth callback message with required fields
      if (event.data.type === 'oauth_callback') {
        // Validate required fields exist
        const hasPopupId = typeof event.data.popup_id === 'string' || typeof event.data.state === 'string';
        const hasSuccess = typeof event.data.success === 'boolean';

        if (!hasPopupId || !hasSuccess) {
logger.warn('UTILS', 'Invalid OAuth callback structure - missing required fields');
          return;
        }
logger.debug('UTILS', 'OAuth callback message received:', {
            receivedPopupId: event.data.popup_id,
            expectedPopupId: expectedPopupId,
            matches: event.data.popup_id === expectedPopupId,
            success: event.data.success
          });

        // Check if popup_id matches OR if it's the state (backend might use state as popup_id)
        if (event.data.popup_id === expectedPopupId || event.data.state === expectedPopupId) {
logger.debug('UTILS', 'Popup ID matched! Processing OAuth result...');
          this.handleMessage(event.data);
        } else {
logger.warn('UTILS', 'Popup ID mismatch - ignoring message');
        }
      }
    };
    
    window.addEventListener('message', this.messageListener, false);
  }
  
  handleMessage(data) {
logger.debug('UTILS', '🔴 OAuth Handler - Processing message:', data);

    // Clear timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    // Actively try to close the popup from parent window
    if (this.popup && !this.popup.closed) {
logger.debug('UTILS', '🔵 Attempting to close popup from parent window');
      try {
        this.popup.close();
      } catch (e) {
        logger.warn('UTILS', 'Failed to close popup from parent:', e.message);
      }
    }
    if (data.success) {
logger.debug('UTILS', '✅ OAuth successful:', data.email);
      // Resolve the promise with success data
      if (this.resolveCallback) {
        this.resolveCallback({
          success: true,
          email: data.email,
          message: data.message || 'Calendar connected successfully'
        });
      }
    } else {
logger.error('UTILS', '❌ OAuth failed:', data.message, data);
      // Reject the promise with error
      if (this.rejectCallback) {
        this.rejectCallback(new Error(data.message || 'Failed to complete authorization'));
      }
    }

    this.cleanup();
  }
  
  monitorPopup() {
    this.checkInterval = setInterval(() => {
      if (this.popup && this.popup.closed) {
logger.debug('UTILS', '🔴 Popup closed by user');
        // If popup was closed without completing OAuth
        if (this.rejectCallback) {
          this.rejectCallback(new Error('OAuth cancelled - popup closed by user'));
        }
        this.cleanup();
      }
    }, 1000);
  }
  
  cleanup() {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    this.popup = null;
    this.resolveCallback = null;
    this.rejectCallback = null;
  }
}